import { enumerate } from 'utils/strings';

import MenuItem from './MenuItem';
import MealTypes from './MealTypes';
import Plan from './Plan';
import FulfillmentStatus from './FulfillmentStatus';

import type { IMenuItem } from './MenuItem';
import type { PlanName } from './Plan';
import type { IAddress } from './Address';
import type { IGroupedSkus } from './GroupedSkus';

enum ArrivingStatus {
  ARRIVING = 'arriving',
  SCHEDULED = 'scheduled',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  SUSPENDED = 'suspended',
  PAUSED = 'paused',
  RETURNING = 'returning',
  ADMIN_ORDER_CANCELLED = 'admin_order_cancelled',
}

type MealSummary = {
  name: string;
  price: number;
  count: number;
};

type Shipment = {
  tracking_number: string;
  delivery_tracking_url: string;
  delivered_at?: Date;
};

interface IUpcomingMenu {
  menu_id: number;
  cart_id: number;
  cart_n_of_plan: number;
  cart_n_of_plan_desc: string;
  is_thanksgiving_menu: boolean;
  fulfillment_status: FulfillmentStatus;
  delivery_on: Date;
  skip_deadline: Date;
  coupon_discount: string;
  discount_amount: string;
  coupon_amount: string;
  sales_tax: string;
  shipping_price: string;
  shipping_fee: string;
  price: string;
  arriving_status: ArrivingStatus;
  upcoming_menu_items: IMenuItem[];
  plan_name: PlanName;
  credit: string;
  meals_summaries: MealSummary[];
  recipient_email: string;
  shipping_address: IAddress;
  shipments: Shipment[];
  modifiable: boolean;
  planned_or_confirmed: boolean;
  past: boolean;
  current_week_menu: boolean;
  menu_status_message: string;
  menu_type: 'WeeklyMenu';
  overridden_recipes: boolean;
  grouped_skus?: IGroupedSkus;

  plan?: Plan;
  areDetailsExpanded?: boolean;
  isGalleryExpanded?: boolean;

  readonly isModifiable?: boolean;
  readonly isEnabled?: boolean;
  readonly isPast?: boolean;
  readonly subtotalPrice?: number;
  readonly shippingPrice?: number;
  readonly mealSummary?: string;
  readonly deliveredAt?: Date;
}

class UpcomingMenu implements IUpcomingMenu {
  menu_id: number;
  cart_id: number;
  cart_n_of_plan: number;
  cart_n_of_plan_desc: string;
  is_thanksgiving_menu: boolean;
  fulfillment_status: FulfillmentStatus;
  delivery_on: Date;
  skip_deadline: Date;
  coupon_discount: string;
  discount_amount: string;
  coupon_amount: string;
  sales_tax: string;
  crd_fee: string;
  shipping_price: string;
  shipping_surcharge: string;
  shipping_fee: string;
  price: string;
  arriving_status: ArrivingStatus;
  upcoming_menu_items: MenuItem[];
  plan_name: PlanName;
  credit: string;
  meals_summaries: MealSummary[];
  recipient_email: string;
  shipping_address: IAddress;
  shipments: Shipment[];
  modifiable: boolean;
  planned_or_confirmed: boolean;
  past: boolean;
  current_week_menu: boolean;
  menu_status_message: string;
  menu_type: 'WeeklyMenu';
  overridden_recipes: boolean;
  grouped_skus?: IGroupedSkus;

  plan: Plan;
  areDetailsExpanded = false;
  isGalleryExpanded = true;

  constructor(attributes: IUpcomingMenu) {
    Object.assign(this, attributes);

    this.plan = new Plan(this.plan_name);

    this.upcoming_menu_items = this.upcoming_menu_items.map(
      (item) => new MenuItem(item),
    ) ?? [];

    this.sortUpcomingMenuItems();
  }

  sortUpcomingMenuItems(): void {
    const mealTypeOrder = {};

    MealTypes.keys(this.plan_name).forEach((meal, i) => {
      mealTypeOrder[meal] = i;
    }, {});

    this.upcoming_menu_items.sort((a, b) => (
      mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType]
    ));
  }

  get isModifiable(): boolean {
    return this.modifiable;
  }

  get isEnabled(): boolean {
    return this.planned_or_confirmed;
  }

  get isPast(): boolean {
    return this.past;
  }

  get subtotalPrice(): number {
    return this.upcoming_menu_items.reduce((acc, cur) => (
      acc + Number.parseFloat(cur.sku.price) * cur.quantity
    ), 0);
  }

  get shippingPrice(): number {
    return Number.parseFloat(this.shipping_price.toString());
  }

  get mealSummary(): string {
    const mealCounts = {};
    const mealStrings = [];

    this.upcoming_menu_items.forEach((item) => {
      const { product_type: key } = item.sku;
      mealCounts[key] = mealCounts[key] !== undefined
        ? mealCounts[key] + item.quantity
        : item.quantity;
    });

    Object.keys(mealCounts).forEach((key) => {
      const mealTypeCount = mealCounts[key];
      const mealTypeName = MealTypes.pluralize(key, mealTypeCount);
      // @ts-expect-error TODO: fix null safety
      mealStrings.push(`${mealTypeCount} ${mealTypeName}`);
    });

    return enumerate(mealStrings);
  }

  get deliveredAt(): Date | undefined {
    if (!this.shipments?.length) return undefined;

    const shipment = this.shipments.find((s) => s.delivered_at);

    return shipment ? new Date(shipment?.delivered_at || '') : undefined;
  }
}

export { ArrivingStatus };
export type { IUpcomingMenu, MealSummary, Shipment };
export default UpcomingMenu;
